import type { AdyenFinalizePaymentResponse } from './adyen';
import { ProviderEnum } from './common';
import {
  FinalizeStatus,
  type BrainTreePaymentResponse,
  type NuveiPaymentResponse,
  type PaypalPaymentResponse,
  type StripePaymentResponse,
} from './payment';

// PSP containers that may carry a Forter `managedOrderToken`. The token nests
// inside the provider object (e.g. `response.stripe.managedOrderToken`,
// `response.adyen.managedOrderToken`), so it's resolved provider-agnostically.
const PSP_RESPONSE_KEYS = ['stripe', 'adyen', 'nuvei', 'braintree', 'paypal'] as const;

const getNestedPspString = (results: unknown, field: string): string | undefined => {
  if (!results || typeof results !== 'object') {
    return undefined;
  }
  for (const key of PSP_RESPONSE_KEYS) {
    const psp = (results as Record<string, unknown>)[key];
    if (psp && typeof psp === 'object' && field in psp) {
      const value = (psp as Record<string, unknown>)[field];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
  }
  return undefined;
};

/**
 * Returns the Forter `managedOrderToken` when the finalize response asks the
 * FE to run a Forter-orchestrated 3DS challenge, otherwise `undefined`. The
 * token lives inside the PSP container of the response (Forter sits in front
 * of the PSP), so this is checked before PSP dispatch.
 */
export const getManagedOrderToken = (results: unknown): string | undefined =>
  getNestedPspString(results, 'managedOrderToken');

/**
 * Returns the BE-issued `paymentAttemptId` that pairs with a Forter
 * `managedOrderToken`. Sent back to `/3ds/complete` once the SDK callback
 * fires so the BE can finalize the correct attempt. Nests under the PSP
 * container, same as `managedOrderToken`.
 */
export const getPaymentAttemptId = (results: unknown): string | undefined =>
  getNestedPspString(results, 'paymentAttemptId');

/**
 * Returns `true` only when the finalize response actually requires the FE
 * to mount a Forter-orchestrated 3DS challenge: a `managedOrderToken` is
 * present **and** the paired PSP `status` is `REDIRECT`, both inside the
 * **same** PSP container (e.g. `response.stripe.{managedOrderToken,status}`).
 *
 * A `managedOrderToken` that arrives alongside a terminal `SUCCESS`/`FAILED`
 * status means the BE already resolved the attempt (frictionless / declined
 * upstream); the response should flow through the standard PSP dispatch
 * instead of mounting <ForterCard3DSForm/>.
 */
export const isForter3DSChallengeRequired = (results: unknown): boolean => {
  if (!results || typeof results !== 'object') return false;
  for (const key of PSP_RESPONSE_KEYS) {
    const psp = (results as Record<string, unknown>)[key];
    if (!psp || typeof psp !== 'object') continue;
    const token = (psp as Record<string, unknown>).managedOrderToken;
    const status = (psp as Record<string, unknown>).status;
    if (typeof token === 'string' && token.length > 0 && status === FinalizeStatus.REDIRECT) {
      return true;
    }
  }
  return false;
};

export const isStripePaymentResponse = (response: unknown): response is StripePaymentResponse => {
  return Boolean(
    response && typeof response === 'object' && 'stripe' in response && typeof response.stripe === 'object'
  );
};

export const isStripeSuccessResponse = (results: unknown, provider: ProviderEnum): results is StripePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.STRIPE &&
    isStripePaymentResponse(results) &&
    results.stripe.status === FinalizeStatus.SUCCESS
  );
};

export const isStripeFailedResponse = (results: unknown, provider: ProviderEnum): results is StripePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.STRIPE &&
    isStripePaymentResponse(results) &&
    (results.stripe.status === FinalizeStatus.FAILED ||
      (results as StripePaymentResponse)?.appChargeErrorType !== undefined)
  );
};

export const isAdyenFinalizePaymentResponse = (results: unknown): results is AdyenFinalizePaymentResponse => {
  return Boolean(results && typeof results === 'object' && 'adyen' in results && typeof results.adyen === 'object');
};

export const isAdyenRedirectResponse = (results: AdyenFinalizePaymentResponse): boolean => {
  return results.adyen.status === FinalizeStatus.REDIRECT;
};

export const isAdyenFailedResponse = (results: AdyenFinalizePaymentResponse): boolean => {
  return results.adyen.status === FinalizeStatus.FAILED;
};

export const isAdyenSuccessResponse = (results: AdyenFinalizePaymentResponse): boolean => {
  return results.adyen.status === FinalizeStatus.SUCCESS;
};

export const isStripeRedirectResponse = (
  results: unknown,
  provider: ProviderEnum
): results is StripePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.STRIPE &&
    isStripePaymentResponse(results) &&
    results.stripe.status === FinalizeStatus.REDIRECT
  );
};

export const isBrainTreePaymentResponse = (results: unknown): results is BrainTreePaymentResponse => {
  return Boolean(
    results &&
    typeof results === 'object' &&
    'braintree' in results &&
    typeof results.braintree === 'object' &&
    results.braintree &&
    'status' in results.braintree &&
    typeof results.braintree.status === 'string'
  );
};

export const isBrainTreeSuccessResponse = (
  results: unknown,
  provider: ProviderEnum
): results is BrainTreePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.BRAINTREE &&
    isBrainTreePaymentResponse(results) &&
    results.braintree.status === FinalizeStatus.SUCCESS &&
    results.orderId
  );
};

export const isBrainTreeFailedResponse = (
  results: unknown,
  provider: ProviderEnum
): results is BrainTreePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.BRAINTREE &&
    isBrainTreePaymentResponse(results) &&
    (results.braintree.status === FinalizeStatus.FAILED ||
      (results as BrainTreePaymentResponse)?.appChargeErrorType !== undefined)
  );
};

export const isBrainTreeRedirectResponse = (
  results: unknown,
  provider: ProviderEnum
): results is BrainTreePaymentResponse => {
  return Boolean(
    provider === ProviderEnum.BRAINTREE &&
    isBrainTreePaymentResponse(results) &&
    results.braintree.status === FinalizeStatus.REDIRECT
  );
};

export const isNuveiSuccessResponse = (results: unknown, provider: ProviderEnum): results is NuveiPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.NUVEI &&
    results &&
    typeof results === 'object' &&
    'nuvei' in results &&
    typeof results.nuvei === 'object' &&
    results.nuvei &&
    'status' in results.nuvei &&
    results.nuvei.status === FinalizeStatus.SUCCESS
  );
};

export const isNuveiFailedResponse = (results: unknown, provider: ProviderEnum): results is NuveiPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.NUVEI &&
    results &&
    typeof results === 'object' &&
    'nuvei' in results &&
    typeof results.nuvei === 'object' &&
    results.nuvei &&
    'status' in results.nuvei &&
    results.nuvei.status === FinalizeStatus.FAILED
  );
};

export const isNuveiPendingResponse = (results: unknown, provider: ProviderEnum): results is NuveiPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.NUVEI &&
    results &&
    typeof results === 'object' &&
    'nuvei' in results &&
    typeof results.nuvei === 'object' &&
    results.nuvei &&
    'status' in results.nuvei &&
    results.nuvei.status === FinalizeStatus.PENDING
  );
};

export const isNuveiRedirectResponse = (results: unknown, provider: ProviderEnum): results is NuveiPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.NUVEI &&
    results &&
    typeof results === 'object' &&
    'nuvei' in results &&
    typeof results.nuvei === 'object' &&
    results.nuvei &&
    'status' in results.nuvei &&
    results.nuvei.status === FinalizeStatus.REDIRECT &&
    'sessionToken' in results
  );
};
export const isPaypalSuccessResponse = (results: unknown, provider: ProviderEnum): results is PaypalPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.PAYPAL &&
    results &&
    typeof results === 'object' &&
    'paypal' in results &&
    typeof results.paypal === 'object' &&
    results.paypal &&
    'status' in results.paypal &&
    results.paypal.status === FinalizeStatus.SUCCESS
  );
};

export const isPaypalFailedResponse = (results: unknown, provider: ProviderEnum): results is PaypalPaymentResponse => {
  return Boolean(
    provider === ProviderEnum.PAYPAL &&
    results &&
    typeof results === 'object' &&
    'paypal' in results &&
    typeof results.paypal === 'object' &&
    results.paypal &&
    'status' in results.paypal &&
    results.paypal.status === FinalizeStatus.FAILED
  );
};
